import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { graphql } from '../../../../../../gql';
import { useAuth } from '@calefy-inc/authentication';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import styled from '@mui/material/styles/styled';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Bugsnag from '@bugsnag/js';
import { errorify } from '../../../../../../util';
import { GET_TASK_POOL } from '../../../../../../queries/queries/getTaskPool';
import { DisplayInlineError } from '../../../../../common/ErrorHandling';
import { SupportLink } from '../../../../../common/SupportLink';
import {
  ClientDataCsvInfo,
  getDateRangeError,
  parseClientDataCsvInfo,
  triggerFileDownload,
} from './clientDataDownload';

/**
 * "Download Client Data" button for the Policystream application list.
 *
 * Opens a modal where the user can pick an optional date range, kicks off the
 * backend CSV generation (a task pool), polls until it finishes and then
 * downloads the CSV automatically, leaving a link in the modal to download it
 * again. Closing the modal does not cancel that task or discard the link.
 */
export function PolicystreamDownloadClientCsvButton() {
  const [open, setOpen] = useState(false);
  const { token } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [taskPoolId, setTaskPoolId] = useState<string | null>(null);
  const [csvInfo, setCsvInfo] = useState<ClientDataCsvInfo | null>(null);
  const [downloadError, setDownloadError] = useState<Error | null>(null);
  const downloadedTaskPoolId = useRef<string | null>(null);

  const [
    generateClientDataCsv,
    { loading: starting, error: startError, reset },
  ] = useMutation(GENERATE_POLICYSTREAM_CLIENT_DATA_CSV);
  const { data: taskPoolData, error: taskPoolQueryError } = useQuery(
    GET_TASK_POOL,
    {
      variables: { taskPoolId: taskPoolId ?? '' },
      skip: !taskPoolId,
      pollInterval: taskPoolId && !csvInfo ? 500 : 0,
      fetchPolicy: 'network-only',
    },
  );
  const poolErrorMessage = taskPoolData?.getTaskPool?.error;
  const taskPoolError = useMemo(() => {
    if (taskPoolQueryError) {
      return taskPoolQueryError;
    }
    if (poolErrorMessage) {
      return new Error(poolErrorMessage);
    }
    return null;
  }, [taskPoolQueryError, poolErrorMessage]);
  const completionPercent = completionPercentFromTasks(
    taskPoolData?.getTaskPool?.tasks,
  );

  // reset() isn't guaranteed to be referentially stable, so keep it out of the effect dependencies
  const resetMutationRef = useRef(reset);
  resetMutationRef.current = reset;

  const dateRangeErrorMessage = getDateRangeError(startDate, endDate);
  const dateRangeError = useMemo(
    () => (dateRangeErrorMessage ? new Error(dateRangeErrorMessage) : null),
    [dateRangeErrorMessage],
  );
  const inProgress =
    starting || (Boolean(taskPoolId) && !csvInfo && !taskPoolError);

  // once the task pool has finished and the link is available, download it (once)
  useEffect(() => {
    if (!taskPoolId || taskPoolError) {
      return;
    }
    const info = parseClientDataCsvInfo(
      taskPoolData?.getTaskPool?.additionalInformation,
    );
    if (!info) {
      return;
    }
    setCsvInfo(info);
    if (downloadedTaskPoolId.current === taskPoolId) {
      return;
    }
    downloadedTaskPoolId.current = taskPoolId;
    try {
      triggerFileDownload(info.url, info.filename);
    } catch (e) {
      const error = errorify(e);
      Bugsnag.notify(error);
      setDownloadError(error);
    }
  }, [taskPoolData, taskPoolError, taskPoolId]);

  useEffect(() => {
    if (taskPoolError) {
      Bugsnag.notify(taskPoolError);
    }
  }, [taskPoolError]);

  const handleGenerate = async () => {
    if (!token || dateRangeError) {
      return;
    }
    setTaskPoolId(null);
    setCsvInfo(null);
    setDownloadError(null);
    downloadedTaskPoolId.current = null;
    resetMutationRef.current();
    try {
      const result = await generateClientDataCsv({
        variables: {
          token,
          startDate: startDate || null,
          endDate: endDate || null,
        },
      });
      const newTaskPoolId =
        result.data?.generatePolicystreamClientDataCsv?.taskPool?.id;
      if (newTaskPoolId) {
        setTaskPoolId(String(newTaskPoolId));
      }
    } catch (e) {
      // displayed via startError
      Bugsnag.notify(errorify(e));
    }
  };

  const onClose = () => setOpen(false);

  return (
    <>
      <Button
        variant='outlined'
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={() => setOpen(true)}
      >
        Download Client Data
      </Button>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth='xs'
        aria-labelledby='download-client-data-title'
      >
        <DialogTitle id='download-client-data-title'>
          Download Client Data
        </DialogTitle>
        <DialogContent>
          <DialogBody spacing={2}>
            <Typography variant='body2' color='text.secondary'>
              Download a CSV of client details for your applications. Optionally
              limit it to applications last modified within a date range; leave
              the dates blank to include everything.
            </Typography>
            <OptionalDateField
              label='Start Date'
              value={startDate}
              onChange={setStartDate}
              disabled={inProgress}
              max={endDate || undefined}
            />
            <OptionalDateField
              label='End Date'
              value={endDate}
              onChange={setEndDate}
              disabled={inProgress}
              min={startDate || undefined}
            />
            <DisplayInlineError error={dateRangeError} />

            <DisplayInlineError error={startError} />

            {inProgress ? (
              <Stack spacing={1}>
                <Typography variant='body2'>Generating CSV…</Typography>
                <LinearProgress
                  variant={
                    completionPercent > 0 ? 'determinate' : 'indeterminate'
                  }
                  value={completionPercent}
                />
              </Stack>
            ) : null}

            <DisplayInlineError error={taskPoolError}>
              There was an error generating the client data CSV. Please try
              again. If the error persists, contact our support team at{' '}
              <SupportLink
                subject='Error generating client data CSV'
                body={taskPoolError?.message}
              />
              .
            </DisplayInlineError>

            <DisplayInlineError error={downloadError}>
              The CSV is ready, but it could not be downloaded automatically.
              Use the link below to download it.
            </DisplayInlineError>

            {csvInfo ? (
              <Alert severity={csvInfo.failedCount ? 'warning' : 'success'}>
                {`The CSV is ready${
                  csvInfo.rowCount !== null
                    ? ` (${csvInfo.rowCount} application${
                        csvInfo.rowCount === 1 ? '' : 's'
                      })`
                    : ''
                }. `}
                {csvInfo.failedCount
                  ? `${csvInfo.failedCount} application${
                      csvInfo.failedCount === 1 ? '' : 's'
                    } could not be included. `
                  : ''}
                <Link
                  href={csvInfo.url}
                  download={csvInfo.filename}
                  rel='noopener'
                >
                  Click here to download it
                </Link>{' '}
                {downloadError ? '' : 'again if needed. '}
                This link expires in an hour.
              </Alert>
            ) : null}
          </DialogBody>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <LoadingButton
            variant='contained'
            startIcon={<DownloadRoundedIcon />}
            loading={inProgress}
            disabled={!token || Boolean(dateRangeError)}
            onClick={handleGenerate}
          >
            {csvInfo ? 'Generate Again' : 'Generate CSV'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface OptionalDateFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  min?: string;
  max?: string;
}

function OptionalDateField({
  label,
  value,
  onChange,
  disabled,
  min,
  max,
}: OptionalDateFieldProps) {
  return (
    <TextField
      fullWidth
      size='small'
      type='date'
      label={`${label} (optional)`}
      InputLabelProps={{ shrink: true }}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ min, max }}
      InputProps={{
        endAdornment: value ? (
          <InputAdornment position='end'>
            <Tooltip title={`Clear ${label.toLowerCase()}`}>
              <span>
                <IconButton
                  size='small'
                  aria-label={`Clear ${label.toLowerCase()}`}
                  onClick={() => onChange('')}
                  disabled={disabled}
                  edge='end'
                >
                  <ClearIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}

const DialogBody = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

const GENERATE_POLICYSTREAM_CLIENT_DATA_CSV = graphql(`
  mutation generatePolicystreamClientDataCsv(
    $token: String!
    $startDate: Date
    $endDate: Date
  ) {
    generatePolicystreamClientDataCsv(
      token: $token
      startDate: $startDate
      endDate: $endDate
    ) {
      taskPool {
        id
      }
    }
  }
`);

type TaskProgress = {
  numSubtasks?: number | null;
  numCompletedSubtasks?: number | null;
} | null;

function completionPercentFromTasks(
  tasks: ReadonlyArray<TaskProgress> | null | undefined,
): number {
  let total = 0;
  let done = 0;
  for (const task of tasks ?? []) {
    const subtasks = task?.numSubtasks ?? 0;
    if (subtasks > 0) {
      total += subtasks;
      done += task?.numCompletedSubtasks ?? 0;
    }
  }
  if (total === 0) {
    return 0;
  }
  return Math.round((done / total) * 100);
}
